'use client';

/* eslint-disable @next/next/no-img-element */
import ProductCard, { type Product } from '@/components/Product/ProductCard';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useTranslations } from '@/i18n/use-translations';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { CollaborationRuntimeData } from '../types';
import { resolveCollaborationAssetUrl } from '../runtimeData';
import type {
  CollaborationAssetType,
  CollaborationMediaClickArea,
} from '../useCollaborationTracking';
import styles from '../CollaborationPage.module.css';
import CollaborationVideoSoundButton from './CollaborationVideoSoundButton';

const GALLERY_AUTOPLAY_MS = 3000;

export function shouldPauseCollaborationGallery({
  hovered,
  pointerActive,
  focused,
}: {
  hovered: boolean;
  pointerActive: boolean;
  focused: boolean;
}) {
  return hovered || pointerActive || focused;
}

export default function CollaborationMedia({
  data,
  isWeb,
  onProductClick,
  onProductExposure,
  onProductCollect,
  onMediaExposure,
  onMediaClick,
}: {
  data: CollaborationRuntimeData['media'];
  isWeb: boolean;
  onProductClick?: (product: unknown, index: number, title: string) => void;
  onProductExposure?: (product: unknown, index: number, title: string) => void;
  onProductCollect?: (product: unknown, index: number, collectType: string) => void;
  onMediaExposure?: (index: number, assetType: CollaborationAssetType, product?: unknown) => void;
  onMediaClick?: (
    index: number,
    assetType: CollaborationAssetType,
    clickArea: CollaborationMediaClickArea,
    product?: unknown,
  ) => void;
}) {
  const t = useTranslations('activity.campaigns.collaboration');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const galleryFrameRef = useRef<HTMLDivElement | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);
  const timerRef = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pointerActive, setPointerActive] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const videoSrc = resolveCollaborationAssetUrl(isWeb ? data.video.pcVideo : data.video.wapVideo);
  const videoCover = resolveCollaborationAssetUrl(isWeb ? data.video.pcCover : data.video.wapCover);
  const galleryPaused = shouldPauseCollaborationGallery({ hovered, pointerActive, focused });
  const galleryStartIndex = data.video.visible && Boolean(videoSrc) ? 1 : 0;
  const activeGalleryItem = data.gallery.images[activeIndex];
  const activeGalleryMktIndex = galleryStartIndex + activeIndex;
  const { ref: videoExposureRef } = useValidExposure({
    callback: () => onMediaExposure?.(0, '视频'),
    threshold: 0.5,
    minDuration: 500,
    ts: videoSrc,
  });
  const { ref: galleryExposureRef } = useValidExposure({
    callback: () => onMediaExposure?.(activeGalleryMktIndex, '图片', activeGalleryItem?.product),
    threshold: 0.5,
    minDuration: 500,
    ts: activeGalleryItem?.image || activeIndex,
  });
  const setGalleryFrameRef = useCallback(
    (node: HTMLDivElement | null) => {
      galleryFrameRef.current = node;
      galleryExposureRef(node);
    },
    [galleryExposureRef],
  );

  useEffect(() => {
    const galleryFrame = galleryFrameRef.current;
    if (!galleryFrame) return;
    const handleMouseEnter = () => setHovered(true);
    const handleMouseLeave = () => {
      setHovered(false);
      setPointerActive(false);
    };
    galleryFrame.addEventListener('mouseenter', handleMouseEnter);
    galleryFrame.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      galleryFrame.removeEventListener('mouseenter', handleMouseEnter);
      galleryFrame.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [data.gallery.images.length, data.gallery.visible]);

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || galleryPaused || !data.gallery.visible || data.gallery.images.length < 2) return;
    timerRef.current = window.setInterval(
      () => swiperRef.current?.slideNext(),
      GALLERY_AUTOPLAY_MS,
    );
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [data.gallery.images.length, data.gallery.visible, galleryPaused]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        videoRef.current?.pause();
        setPlaying(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting && !video.paused) video.pause();
      },
      { threshold: 0.2 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [data.video.visible, videoSrc]);

  const toggleVideo = async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      onMediaClick?.(0, '视频', '播放按钮', undefined);
      try {
        await video.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  if (!data.video.visible && !data.gallery.visible) return null;

  return (
    <section className={styles.mediaSection}>
      {data.video.visible && videoSrc && (
        <article className={styles.mediaPanel}>
          {data.video.title && <h2>{data.video.title}</h2>}
          <div ref={videoExposureRef} className={styles.videoFrame}>
            <video
              ref={videoRef}
              src={videoSrc}
              poster={videoCover || undefined}
              muted
              playsInline
              aria-hidden="true"
              onClick={toggleVideo}
              onPause={() => {
                setPlaying(false);
              }}
              onPlay={() => setPlaying(true)}
              onEnded={() => {
                setPlaying(false);
              }}
            />
            {!playing ? (
              <button
                className={styles.videoPlayButton}
                type="button"
                onClick={toggleVideo}
                aria-label={t('playVideo')}
              >
                <svg aria-hidden="true" viewBox="0 0 56 56" fill="none">
                  <circle
                    cx="28"
                    cy="28"
                    r="24.45"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M37.9681 27.1419C39.6079 28.0557 39.6139 29.2052 37.9681 30.2383L25.2517 38.8145C23.6539 39.6672 22.5688 39.1637 22.4548 37.3187L22.4009 19.3689C22.3649 17.6693 23.7648 17.1892 25.0988 18.004L37.9681 27.1419Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            ) : null}
            <CollaborationVideoSoundButton key={videoSrc} videoRef={videoRef} />
          </div>
        </article>
      )}
      {data.gallery.visible && data.gallery.images.length > 0 && (
        <article className={styles.mediaPanel}>
          {data.gallery.title && <h2>{data.gallery.title}</h2>}
          <div ref={setGalleryFrameRef} className={styles.galleryFrame} data-collaboration-gallery>
            <Swiper
              loop={data.gallery.images.length > 1}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
              onPointerDown={() => setPointerActive(true)}
              onPointerUp={() => setPointerActive(false)}
              onPointerCancel={() => setPointerActive(false)}
              onPointerLeave={() => setPointerActive(false)}
              onFocusCapture={() => setFocused(true)}
              onBlurCapture={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setFocused(false);
                }
              }}
            >
              {data.gallery.images.map((item, index) => (
                <SwiperSlide key={`${item.sort}-${item.image}`}>
                  <img
                    src={resolveCollaborationAssetUrl(item.image)}
                    alt={`${data.gallery.title} ${index + 1}`.trim()}
                    loading="lazy"
                    decoding="async"
                    data-collaboration-gallery-image
                  />
                  {isWeb && item.product && (
                    <div
                      className={`${styles.galleryProduct} ${styles.galleryProductInteractive}`}
                      data-collaboration-gallery-product
                      data-interaction="pc-hover"
                    >
                      <ProductCard
                        product={item.product as unknown as Product}
                        showTryOn={false}
                        showCorrelation={false}
                        showReviewStar={false}
                        showWish={false}
                        showTag={false}
                        showMember={false}
                        showName
                        showOriginalPrice
                        namePriceInline={false}
                        productImageWhiteBg
                        className={styles.galleryProductCard}
                        imageClassName={styles.galleryProductImage}
                        productInfoClassName={styles.galleryProductInfo}
                        index={item.sort}
                        handelClick={(currentIndex, currentProduct) =>
                          onProductClick?.(currentProduct, currentIndex, data.gallery.title)
                        }
                        onValidExposure={(currentIndex, currentProduct) =>
                          onProductExposure?.(currentProduct, currentIndex, data.gallery.title)
                        }
                        wished={(currentProduct, currentIndex) =>
                          onProductCollect?.(currentProduct, currentIndex, '收藏')
                        }
                        removed={(currentProduct, currentIndex) =>
                          onProductCollect?.(currentProduct, currentIndex, '取消收藏')
                        }
                      />
                    </div>
                  )}
                </SwiperSlide>
              ))}
            </Swiper>
            {data.gallery.images.length > 1 && (
              <>
                <button
                  type="button"
                  className={styles.galleryPrev}
                  aria-label={t('previousImage')}
                  onClick={() => {
                    onMediaClick?.(
                      activeGalleryMktIndex,
                      '图片',
                      '左侧点击',
                      activeGalleryItem?.product,
                    );
                    swiperRef.current?.slidePrev();
                  }}
                >
                  <svg aria-hidden="true" viewBox="0 0 20 20">
                    <path d="m12.5 3.75-6.25 6.25 6.25 6.25" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={styles.galleryNext}
                  aria-label={t('nextImage')}
                  onClick={() => {
                    onMediaClick?.(
                      activeGalleryMktIndex,
                      '图片',
                      '右侧点击',
                      activeGalleryItem?.product,
                    );
                    swiperRef.current?.slideNext();
                  }}
                >
                  <svg aria-hidden="true" viewBox="0 0 20 20">
                    <path d="m7.5 3.75 6.25 6.25-6.25 6.25" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </article>
      )}
    </section>
  );
}
